import { Component, inject, input, output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ENTER, COMMA, E } from '@angular/cdk/keycodes';
import { MaterialModule } from '../../../material/material.module';
import { CommonModule } from '@angular/common';
import { JobsService } from '../../../services/jobs.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../../services/app.service';

@Component({
  selector: 'app-create',
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './create.component.html',
  styleUrl: './create.component.scss',
})
export class CreateComponent {
  fb = inject(FormBuilder);
  jobsService = inject(JobsService);
  appService = inject(AppService);
  private _router = inject(Router);
  snackBar = inject(MatSnackBar);

  closeDrawer = output();

  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  skills: string[] = [];
  loaders: any = {
    addJob: false,
    addCandidate: false,
  };

  jobID = input();

  jobForm: FormGroup = this.fb.group({
    jobTitle: ['', Validators.required],
    description: ['', Validators.required],
    questions: this.fb.array([
      this.fb.group({ question: ['']})
    ])
  });

  candidateForm: FormGroup = this.fb.group({
    name: [null, Validators.required],
    file: [null, Validators.required],
  });

  showCandidateForm = input(false);

  get questionsFormArray() {
    return this.jobForm.get('questions') as FormArray;
  }

  addCandidate() {
    if (this.loaders.addCandidate) return;
    if (this.candidateForm.valid) {
      this.loaders.addCandidate = true;
      const formData = new FormData();
      formData.append('resume', this.candidateForm.value.file);
      formData.append('candidate_name', this.candidateForm.value.name);
      this.jobsService
        .addCandidate(this.jobID(), formData)
        .pipe(finalize(() => (this.loaders.addCandidate = false)))
        .subscribe({
          next: (res: any) => {
            this.snackBar.open(res.message);
            this.candidateForm.reset();
            this.candidateForm.clearValidators();
            this.loaders.addCandidate = false;
            this.appService.loadCreds();
            this.closeDrawer.emit();
          },
          error: (err: any) => {
            this.snackBar.open(err.error.message || 'Unable to add candidate');
          },
        });
    }
  }

  onFileChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.candidateForm.patchValue({ file: file });
    }
  }

  addJob(): void {
    if (this.loaders.addJob) return;
    this.loaders.addJob = true;
    if (this.jobForm.valid) {
      const questions = this.questionsFormArray.value.map((q: any) => q.question);
      const payload = {
        position: this.jobForm.value.jobTitle,
        job_description: this.jobForm.value.description,
        questions
      };
      this.jobsService
        .addJob(payload)
        .pipe(finalize(() => (this.loaders.addJob = false)))
        .subscribe({
          next: (res: any) => {
            this.snackBar.open(res.message || 'Unable to add job');
            this.jobForm.reset();
            this.jobForm.clearValidators();
            this.jobForm.markAsPristine();
            this.closeDrawer.emit()
          },
        });
    }
  }

  addQuestion() {
    this.questionsFormArray.push(
      this.fb.group({
        question: [null]
      })
    );
  }
}
